import { useEffect, useMemo, useState } from "react";
import { Grid, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Button, LoadingOverlay } from "@finpay-development/shared-components";
import { clearPatient } from "@finpay-development/shared-platform/lib/esm/admissions-advisor/state/vob-patient-slice";
import {
  clearFacilityPayers,
} from "@finpay-development/shared-platform/lib/esm/admissions-advisor/state/vob-slice";
import { clearRiskAssessment } from "@finpay-development/shared-platform/lib/esm/admissions-advisor/state/risk-assessment-slice";
import { PatientEncounterCard } from "@finpay-development/shared-platform/lib/esm/patient/components/models/patient-encounter-card";
import { workFlowStatus } from "@finpay-development/shared-platform/lib/esm/patient/components/models/workflow-status";
import {
  clearComments,
  clearSelectedEncounter,
} from "@finpay-development/shared-platform/lib/esm/patient/state/patient-slice";
import {
  getInstanceOfCare,
  getPatientDemographics,
} from "@finpay-development/shared-platform/lib/esm/patient/state/patient-thunk";
import {
  clearPatientVobList,
  clearSelectedVob,
} from "@finpay-development/shared-platform/lib/esm/shared/state/vob-slice";
import {
  clearSavedEstimatesList,
  clearSelectedEstimate,
} from "@finpay-development/shared-platform/lib/esm/shared/state/estimator-slice";
import { StatusColors } from "@finpay-development/shared-platform/lib/esm/shared/enums";
import { Utils } from "@finpay-development/shared-platform/lib/esm/shared/utils";
import { RootState } from "../shared/state/root-reducer";
import { AppDispatch } from "../shared/state/store";
import {
  getBoardStatusIdsJoined,
  getBoardWorkflowStatuses,
} from "./board-columns";
import IocBoard, { IocBoardCardDisplay } from "./ioc-board";
import {
  DEFAULT_IOC_QUERY_ID,
  getIocQueryById,
  IOC_QUERIES,
  IocQueryId,
  resolveIocQueryForBoard,
} from "./ioc-queries";
import IocQueryNav from "./ioc-query-nav";
import { loadIocQuery } from "./load-ioc-query";

function getStatusText(patient: PatientEncounterCard) {
  const statusId = patient?.workflow?.workflowStatus?.workflowStatusId;
  const statusDesc = patient?.workflow?.workflowStatus?.workflowStatusDesc || "";
  const subDesc = patient?.workflow?.workflowSubStatus?.workflowSubStatusDesc;
  const subId = patient?.workflow?.workflowSubStatus?.workflowSubStatusId;
  // Full PE often returns substatus "None" — treat as empty and use primary status
  const meaningfulSub =
    subDesc && subDesc !== "None" ? subDesc : "";

  if (statusId === 14 || statusId === 15) {
    const matched = workFlowStatus.preconvert.find(
      (status) => status?.workflowSubStatus.workflowSubStatusId === subId
    );
    return (
      matched?.workflowSubStatus?.workflowSubStatusName ||
      meaningfulSub ||
      statusDesc ||
      (statusId === 15 ? "Closed" : "Follow Up Needed")
    );
  }
  if (statusId === 8 || statusId === 12) {
    return "Good Standing";
  }
  if (statusId === 13 || statusId == null || statusId === 0) {
    return meaningfulSub || statusDesc || "New";
  }
  if (statusId === 16) {
    return "Paid";
  }
  return meaningfulSub || statusDesc;
}

function hasMiddleName(patient: PatientEncounterCard) {
  return !!patient?.contactCard?.middleName;
}

function formatPatientName(patient: PatientEncounterCard) {
  const first = patient?.contactCard?.firstName || "";
  const middle = hasMiddleName(patient)
    ? `${patient.contactCard.middleName} . `
    : "";
  const last = patient?.contactCard?.lastName || "";
  return `${first} ${middle}${last}`.trim();
}

function formatStatusText(patient: PatientEncounterCard) {
  return patient.clientFacilityName || "";
}

function formatPfrTimingPayor(patient: PatientEncounterCard) {
  const parts: string[] = [];
  if (patient?.pfrAmt) {
    parts.push(`Pfr: $${patient.pfrAmt.toLocaleString()}`);
  }
  if (patient?.timingRisk?.timingRiskName) {
    parts.push(patient.timingRisk.timingRiskName);
  }
  if (patient?.payorRisk?.payorRiskName) {
    parts.push(patient.payorRisk.payorRiskName);
  }
  return parts.join(" ");
}

function getCardDisplay(patient: PatientEncounterCard): IocBoardCardDisplay {
  return {
    title: formatPatientName(patient),
    statusText: formatStatusText(patient),
    subtitle: getStatusText(patient) || "",
    admissionDate: patient?.admissionDt,
    reminderDate: patient?.completionDueDt,
    pfrTimingRiskPayorRisk: formatPfrTimingPayor(patient),
    riskClassName: patient?.riskClass?.riskClassName,
    statusColor: patient.cardColor || StatusColors.gray,
  };
}

const PatientRecordsPage = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const workflows = useSelector((state: RootState) => state.metaData.workflow);
  const isMetadataLoading = useSelector(
    (state: RootState) => state.metaData.isLoading.metadata
  );

  const boardColumns = useMemo(
    () => getBoardWorkflowStatuses(workflows),
    [workflows]
  );
  const boardStatusIdsJoined = useMemo(
    () => getBoardStatusIdsJoined(boardColumns),
    [boardColumns]
  );

  const [selectedQueryId, setSelectedQueryId] = useState<IocQueryId>(
    DEFAULT_IOC_QUERY_ID
  );
  const [cards, setCards] = useState<PatientEncounterCard[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Same reset/navigate flow as the Patient tab "New" button on /patients
  const handleAddPatient = () => {
    Utils.resetPatientContexts(dispatch);
    dispatch(clearPatient());
    dispatch(clearSelectedVob());
    dispatch(clearPatientVobList());
    dispatch(clearSelectedEstimate());
    dispatch(clearSavedEstimatesList());
    dispatch(clearFacilityPayers());
    dispatch(clearRiskAssessment());
    dispatch(clearSelectedEncounter());
    dispatch(clearComments());
    navigate("/patients", { replace: true });
  };

  const handleIocTileClick = async (patient: PatientEncounterCard) => {
    const patientId = patient?.patientId;
    const encounterId = patient?.patientEncounterId;
    if (!patientId) {
      return;
    }

    dispatch(clearSelectedEncounter());
    await dispatch(getPatientDemographics(patientId));
    if (encounterId) {
      await dispatch(getInstanceOfCare({ patientId, encounterId }));
    }
    navigate(`/patients/${patientId}`);
  };

  useEffect(() => {
    let cancelled = false;
    const baseQuery = getIocQueryById(selectedQueryId);
    const query = resolveIocQueryForBoard(baseQuery, boardStatusIdsJoined);

    const loadCards = async () => {
      setIsLoading(true);
      setErrorMessage(null);
      try {
        const result = await loadIocQuery(query);
        if (cancelled) {
          return;
        }
        setErrorMessage(result.errorMessage);
        setCards(result.cards);
      } catch (err) {
        if (!cancelled) {
          setErrorMessage(
            err instanceof Error
              ? err.message
              : "Failed to load patient records"
          );
          setCards([]);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadCards();
    return () => {
      cancelled = true;
    };
  }, [selectedQueryId, boardStatusIdsJoined]);

  return (
    <main>
      <div className="content fullwidth px-4 py-4">
        <Grid container justifyContent="flex-end" className="mb-4">
          <Button onClick={handleAddPatient}>Add Patient</Button>
        </Grid>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={4} md={3} lg={2}>
            <IocQueryNav
              queries={IOC_QUERIES}
              selectedQueryId={selectedQueryId}
              onSelectQuery={setSelectedQueryId}
            />
          </Grid>
          <Grid item xs={12} sm={8} md={9} lg={10}>
            {(isLoading || isMetadataLoading) && <LoadingOverlay />}
            {errorMessage && (
              <Typography color="error" className="mb-4">
                {errorMessage}
              </Typography>
            )}
            {!isLoading &&
              !isMetadataLoading &&
              !errorMessage &&
              cards.length === 0 && (
                <Typography className="mb-4">
                  No patient records found.
                </Typography>
              )}
            {!isLoading && !isMetadataLoading && (
              <IocBoard
                columns={boardColumns}
                cards={cards}
                getCardDisplay={getCardDisplay}
                onCardClick={handleIocTileClick}
                onCardsChange={setCards}
                onStatusError={setErrorMessage}
              />
            )}
          </Grid>
        </Grid>
      </div>
    </main>
  );
};

export default PatientRecordsPage;
